import React from 'react';
import { useFloating, useInteractions, useRole, useTransitionStatus, useDismiss } from '@floating-ui/react';
import { DialogHookProps, DialogOpenChangeReason } from './Dialog.types';
import { useHoneycombTheme } from '../../theming';
import { usePrevious } from '../../hooks';

export function useDialog(props: DialogHookProps) {
    const {
        open: openProp,
        defaultOpen = false,
        onOpenChange,
        onClose,
        role: roleProp = 'dialog',
        onExited,
        dismissable = true,
        loading = false,
        withAnimation = true,
    } = props;

    const isControlled = openProp !== undefined;
    const [openState, setOpenState] = React.useState(defaultOpen);

    const [labelId, setLabelId] = React.useState<string | undefined>();
    const [descriptionId, setDescriptionId] = React.useState<string | undefined>();

    const getOpen = React.useCallback(() => {
        return isControlled ? openProp : openState;
    }, [isControlled, openProp, openState]);

    const handleOpenChange = React.useCallback(
        (newOpen: boolean, event?: Event, reason?: DialogOpenChangeReason) => {
            if (!isControlled) {
                setOpenState(newOpen);
            }
            onOpenChange?.(newOpen, event, reason);
            if (!newOpen) {
                onClose?.(event, reason);
            }
        },
        [isControlled, onClose, onOpenChange]
    );

    const data = useFloating({
        open: getOpen(),
        onOpenChange: handleOpenChange,
    });

    const { context } = data;

    const { theme } = useHoneycombTheme();
    const transitionDuration = withAnimation ? theme.components.themes.Dialog.animationDuration : 0;

    const { isMounted, status: transitionStatus } = useTransitionStatus(context, {
        duration: transitionDuration,
    });

    const prevIsMounted = usePrevious<boolean>(isMounted);

    const role = useRole(context, { role: roleProp });

    //  Note: We are handling outside press ourselves in DialogBackdrop.
    //  This solves issues related to stacked modals where previously-opened modals would inadvertently close.
    const dismiss = useDismiss(context, {
        enabled: dismissable,
        escapeKey: true,
        outsidePress: false,
        bubbles: false,
    });

    const interactions = useInteractions([role, dismiss]);

    React.useEffect(() => {
        if (prevIsMounted && !isMounted) {
            onExited?.();
        }
    }, [prevIsMounted, isMounted, onExited]);

    return React.useMemo(
        () => ({
            open: getOpen(),
            handleOpenChange,
            dismissable,
            ...data,
            ...interactions,
            isMounted,
            transitionStatus,
            labelId,
            descriptionId,
            setLabelId,
            setDescriptionId,
            loading,
            withAnimation,
        }),
        [
            getOpen,
            handleOpenChange,
            dismissable,
            data,
            interactions,
            isMounted,
            transitionStatus,
            labelId,
            descriptionId,
            loading,
            withAnimation,
        ]
    );
}
